import { KNOWN_TAGS, type KnownTag, type TagInfo } from './model.ts';

/**
 * Källtaggar från importfilen. En tagg är en uppgift från importkällan – inte ett myndighetsbeslut
 * autentiserat av AutoDay, inte en kategori, inte en prioritet och inte antal dagar kvar.
 */
export const TAG_EXPLANATIONS: Record<KnownTag, string> = {
  '[MÅSTE]': 'Importkällan anger att detta är en skyldighet. AutoDay har inte verifierat myndighetsregeln.',
  '[FÖRBEREDELSE]': 'Importkällan anger ett förberedelsesteg inför en skyldighet. Det är inte frivilligt.',
  '[FRIVILLIG KONTROLL]': 'Importkällan anger en frivillig kontroll. Läs originaltexten för vad som gäller.',
  '[KONTROLLERA]': 'Importkällan ber dig kontrollera uppgiften mot originalet.',
  '[INGEN BETALNING]':
    'Importkällan anger att denna post inte avser en betalning. Det betyder inte att deklarationer eller andra betalningar kan hoppas över – läs originaltexten.',
  '[TEST]': 'Importkällan markerar posten som test.',
};

const PREFIX_RE = /^\s*(\[[^\]\r\n]{1,40}\])/u;

/**
 * Extraherar känd tagg från början av SUMMARY. Okänt prefix eller saknad tagg ger tag = null
 * (neutral/okänd) – aldrig ett påhittat [MÅSTE]. Originaltexten bevaras av anroparen.
 */
export function extractTag(summary: string): TagInfo & { rest: string } {
  const m = PREFIX_RE.exec(summary);
  if (!m) return { tag: null, rawPrefix: null, rest: summary.trim() };
  const raw = m[1]!;
  const normalized = raw.normalize('NFC').toUpperCase();
  const known = (KNOWN_TAGS as readonly string[]).find((t) => t === normalized) as KnownTag | undefined;
  return { tag: known ?? null, rawPrefix: raw, rest: summary.slice(m[0].length).trim() };
}

export function tagExplanation(info: TagInfo): string {
  if (info.tag) return TAG_EXPLANATIONS[info.tag];
  if (info.rawPrefix) return `Okänd tagg ${info.rawPrefix} från importkällan. AutoDay tolkar den inte.`;
  return 'Ingen tagg i källan.';
}

/** Sorteringsvikt för sammanfattningar – bara ordning, inte juridisk prioritet. */
export function tagSortWeight(info: TagInfo): number {
  switch (info.tag) {
    case '[MÅSTE]':
      return 0;
    case '[FÖRBEREDELSE]':
      return 1;
    case '[KONTROLLERA]':
      return 2;
    case '[INGEN BETALNING]':
      return 3;
    case '[FRIVILLIG KONTROLL]':
      return 4;
    case '[TEST]':
      return 6;
    default:
      return 5;
  }
}
